import React, { useEffect, useMemo, useState } from "react";

type NoteCategory =
  | "All"
  | "Foundation"
  | "JEE"
  | "NEET"
  | "Boards"
  | "Quick Revision";

type NoteType =
  | "PDF Notes"
  | "DPP"
  | "Formula Sheet"
  | "Handwritten"
  | "Revision Notes";

type StudyNote = {
  id: number;
  title: string;
  seoTitle: string;
  category: Exclude<NoteCategory, "All">;
  subject: string;
  type: NoteType;
  classLevel: string;
  pages: number;
  downloads: string;
  updated: string;
  description: string;
  image: string;
  alt: string;
  points: string[];
  keywords: string;
};

type NoteBenefit = {
  title: string;
  desc: string;
  keywords: string;
};

type StudyFlow = {
  step: string;
  title: string;
  desc: string;
  keywords: string;
};

const categories: NoteCategory[] = [
  "All",
  "Foundation",
  "JEE",
  "NEET",
  "Boards",
  "Quick Revision",
];

const studyNotes: StudyNote[] = [
  {
    id: 1,
    title: "Class 10 Science Chapter Notes",
    seoTitle:
      "Class 10 Science Chapter-Wise PDF Notes for CBSE Board Exam Preparation",
    category: "Boards",
    subject: "Science",
    type: "PDF Notes",
    classLevel: "Class 10",
    pages: 42,
    downloads: "8.2k",
    updated: "Updated Today",
    description:
      "Complete chapter-wise Class 10 Science notes for CBSE board preparation with definitions, diagrams, important questions, NCERT concepts and quick revision points.",
    image:
      "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=900&h=650&fit=crop&auto=format&q=80",
    alt: "Class 10 Science chapter-wise PDF notes for CBSE board exam preparation and NCERT revision",
    points: ["Chapter summary", "Important questions", "Board exam focus"],
    keywords:
      "Class 10 Science notes, CBSE Class 10 notes, NCERT Science notes, board exam preparation, Science PDF notes, Class 10 important questions",
  },
  {
    id: 2,
    title: "JEE Physics Formula Sheet",
    seoTitle:
      "JEE Physics Formula Sheet for Mechanics Electricity Waves Optics and Modern Physics",
    category: "JEE",
    subject: "Physics",
    type: "Formula Sheet",
    classLevel: "JEE Main",
    pages: 18,
    downloads: "12.4k",
    updated: "Updated Weekly",
    description:
      "A compact JEE Physics formula sheet covering mechanics, electricity, waves, optics and modern physics for fast revision and numerical problem solving.",
    image:
      "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=900&h=650&fit=crop&auto=format&q=80",
    alt: "JEE Physics formula sheet for mechanics electricity waves optics and modern physics revision",
    points: ["Topic-wise formulas", "Fast revision", "Numerical support"],
    keywords:
      "JEE Physics formula sheet, JEE Main Physics formulas, IIT JEE Physics notes, mechanics formulas, electricity formulas, optics formulas, modern physics revision",
  },
  {
    id: 3,
    title: "NEET Biology NCERT Notes",
    seoTitle:
      "NEET Biology NCERT Notes with Diagrams Line-by-Line Highlights and MCQ Points",
    category: "NEET",
    subject: "Biology",
    type: "Revision Notes",
    classLevel: "NEET",
    pages: 65,
    downloads: "15.8k",
    updated: "Updated Today",
    description:
      "NCERT-focused NEET Biology notes with line-by-line highlights, diagrams, key terms, chapter summaries and exam-oriented MCQ preparation points.",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&h=650&fit=crop&auto=format&q=80",
    alt: "NEET Biology NCERT revision notes with diagrams MCQ practice and line by line highlights",
    points: ["NCERT based", "Diagram notes", "MCQ preparation"],
    keywords:
      "NEET Biology notes, NCERT Biology notes, NEET UG Biology revision, Biology diagrams, NEET MCQ practice, Human Physiology notes, Genetics notes, Ecology notes",
  },
  {
    id: 4,
    title: "Foundation Maths Practice DPP",
    seoTitle:
      "Foundation Maths Daily Practice Problems for Class 7 to 9 Students",
    category: "Foundation",
    subject: "Mathematics",
    type: "DPP",
    classLevel: "Class 7 to 9",
    pages: 28,
    downloads: "5.6k",
    updated: "Updated Yesterday",
    description:
      "Daily practice problems for foundation students to improve Maths calculation speed, concept clarity, problem solving and regular practice habits.",
    image:
      "https://images.unsplash.com/photo-1509228627152-72ae9ae6848d?w=900&h=650&fit=crop&auto=format&q=80",
    alt: "Foundation Maths daily practice problems for Class 7 Class 8 and Class 9 students",
    points: ["Daily practice", "Concept questions", "Speed improvement"],
    keywords:
      "Foundation Maths DPP, Class 7 Maths practice, Class 8 Maths worksheet, Class 9 Maths questions, daily practice problems, Maths speed improvement",
  },
  {
    id: 5,
    title: "Chemistry Handwritten Notes",
    seoTitle:
      "Chemistry Handwritten Notes for JEE NEET Reactions Formulas and Short Tricks",
    category: "JEE",
    subject: "Chemistry",
    type: "Handwritten",
    classLevel: "JEE / NEET",
    pages: 54,
    downloads: "9.1k",
    updated: "Updated This Week",
    description:
      "Clean handwritten Chemistry notes covering reactions, formulas, exceptions, short tricks, organic chemistry, inorganic chemistry and important exam points.",
    image:
      "https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=900&h=650&fit=crop&auto=format&q=80",
    alt: "Chemistry handwritten notes for JEE and NEET reactions formulas and exam revision",
    points: ["Short tricks", "Reaction notes", "Exam highlights"],
    keywords:
      "Chemistry handwritten notes, JEE Chemistry notes, NEET Chemistry notes, organic chemistry notes, inorganic chemistry notes, chemical reactions, Chemistry short tricks",
  },
  {
    id: 6,
    title: "Quick Revision Notes Pack",
    seoTitle:
      "Quick Revision Notes Pack for Boards JEE NEET Tests and Last-Minute Study",
    category: "Quick Revision",
    subject: "Mixed Subjects",
    type: "Revision Notes",
    classLevel: "All Students",
    pages: 36,
    downloads: "11.7k",
    updated: "Updated Today",
    description:
      "Short and focused notes designed for last-minute revision before school tests, quizzes, board exams, JEE practice and NEET competitive preparation.",
    image:
      "https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=900&h=650&fit=crop&auto=format&q=80",
    alt: "Quick revision notes pack for board exams JEE NEET quizzes and last minute preparation",
    points: ["Last-minute revision", "Short notes", "Test preparation"],
    keywords:
      "quick revision notes, last minute revision, board exam revision, JEE revision notes, NEET revision notes, short notes, exam preparation notes",
  },
];

const noteBenefits: NoteBenefit[] = [
  {
    title: "Chapter-Wise Notes",
    desc: "Students can access notes arranged by class, subject, chapter and exam goal for faster revision and better concept clarity.",
    keywords:
      "chapter wise notes, subject wise notes, class wise notes, exam focused notes, concept clarity",
  },
  {
    title: "Downloadable PDFs",
    desc: "Notes can be viewed online or downloaded as PDF files for offline revision, homework support and daily study practice.",
    keywords:
      "downloadable PDF notes, offline revision, study material PDF, class notes download, online notes library",
  },
  {
    title: "DPP and Worksheets",
    desc: "Daily practice problems and worksheets help students revise concepts through regular question solving and exam-style practice.",
    keywords:
      "DPP, daily practice problems, worksheets, question practice, exam practice, chapter worksheet",
  },
  {
    title: "Exam-Focused Content",
    desc: "Notes are prepared with important questions, formulas, diagrams, NCERT highlights and quick revision points for better preparation.",
    keywords:
      "exam focused notes, important questions, formulas, diagrams, NCERT highlights, quick revision",
  },
];

const studyFlow: StudyFlow[] = [
  {
    step: "01",
    title: "Choose Subject",
    desc: "Select your class, course or subject from the notes library according to your learning goal.",
    keywords:
      "choose subject, notes library, class notes, course notes, subject wise study material",
  },
  {
    step: "02",
    title: "Read Notes",
    desc: "Understand concepts through clean chapter notes, diagrams, definitions, formulas and examples.",
    keywords:
      "read notes, chapter notes, diagrams, definitions, formulas, examples, concept revision",
  },
  {
    step: "03",
    title: "Practice DPP",
    desc: "Solve daily practice problems and chapter worksheets after completing revision.",
    keywords:
      "practice DPP, daily practice problems, chapter worksheets, question solving, revision practice",
  },
  {
    step: "04",
    title: "Revise Before Test",
    desc: "Use formula sheets and quick notes before quizzes, mock tests, board exams and competitive exams.",
    keywords:
      "revise before test, formula sheets, quick notes, mock test revision, board exam revision",
  },
];

const resources = [
  "PDF Chapter Notes",
  "Handwritten Notes",
  "Formula Sheets",
  "Daily Practice Problems",
  "Board Important Questions",
  "NCERT Highlights",
  "Quick Revision Notes",
  "Topic-Wise Worksheets",
];

const seoKeywords = [
  "Mediit Academy study notes",
  "study notes for students",
  "PDF notes for students",
  "Class 7 notes",
  "Class 8 notes",
  "Class 9 Maths Science notes",
  "Class 10 Science notes",
  "CBSE notes",
  "NCERT notes",
  "board exam notes",
  "JEE notes",
  "JEE Physics formula sheet",
  "JEE Chemistry notes",
  "NEET Biology notes",
  "NEET NCERT notes",
  "daily practice problems",
  "DPP for students",
  "handwritten notes",
  "formula sheets",
  "quick revision notes",
  "chapter wise notes",
  "online notes library",
  "study material PDF",
  "revision material",
  "important questions",
].join(", ");

const getTypeStyle = (type: NoteType) => {
  if (type === "PDF Notes") return "bg-blue-50 text-blue-600 border-blue-100";
  if (type === "DPP") return "bg-green-50 text-green-600 border-green-100";
  if (type === "Formula Sheet")
    return "bg-purple-50 text-purple-600 border-purple-100";
  if (type === "Handwritten")
    return "bg-orange-50 text-orange-600 border-orange-100";
  return "bg-red-50 text-red-600 border-red-100";
};

const StudyNotes: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<NoteCategory>("All");

  useEffect(() => {
    const title =
      "Study Notes | PDF Notes, DPP, Formula Sheets for JEE, NEET, CBSE - Mediit Academy";

    const description =
      "Explore Mediit Academy study notes for Class 7 to 10, CBSE, NCERT, boards, JEE, NEET, DPPs, formula sheets, handwritten notes, quick revision notes and downloadable PDF study material.";

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", seoKeywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", `${window.location.origin}/study-notes`);
  }, []);

  const filteredNotes = useMemo(() => {
    if (activeCategory === "All") return studyNotes;
    return studyNotes.filter((note) => note.category === activeCategory);
  }, [activeCategory]);

  const notesSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Mediit Academy Study Notes",
    description:
      "Study notes library for CBSE, NCERT, boards, JEE, NEET, foundation classes, DPPs, formula sheets, handwritten notes and quick revision notes.",
    url: "/study-notes",
    keywords: seoKeywords,
    mainEntity: {
      "@type": "ItemList",
      name: "Study Notes and Revision Resources",
      itemListElement: studyNotes.map((note, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "LearningResource",
          name: note.seoTitle,
          description: note.description,
          learningResourceType: note.type,
          educationalLevel: note.classLevel,
          about: note.subject,
          keywords: note.keywords,
        },
      })),
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Use Study Notes for Revision and Practice",
    description:
      "A simple workflow for students to choose subject notes, read chapter material, practice DPPs and revise before tests.",
    step: studyFlow.map((item) => ({
      "@type": "HowToStep",
      name: item.title,
      text: item.desc,
    })),
  };

  const goToNotesLibrary = () => {
    window.location.href = "/notes-library";
  };

  const goToDashboard = () => {
    window.location.href = "/student-dashboard";
  };

  const scrollToNotes = () => {
    const section = document.getElementById("notes-library");
    section?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="min-h-screen bg-[#f7f9fc] font-['Inter'] text-[#101827]"
      aria-label="Mediit Academy study notes page"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(notesSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(howToSchema),
        }}
      />

      <main>
        {/* HERO */}
        <section
          className="relative overflow-hidden bg-white font-['Inter']"
          aria-labelledby="study-notes-heading"
        >
          <div className="absolute -left-24 top-0 h-96 w-96 rounded-full bg-blue-100 blur-3xl" />
          <div className="absolute right-0 top-20 h-96 w-96 rounded-full bg-yellow-100 blur-3xl" />
          <div className="absolute bottom-0 left-[42%] h-72 w-72 rounded-full bg-green-100 blur-3xl" />

          <div className="relative mx-auto max-w-[1380px] px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-[#f8fbff] px-4 py-2 shadow-sm">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-black text-white">
                    N
                  </span>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Study Notes Library
                  </p>
                </div>

                <h1
                  id="study-notes-heading"
                  className="mt-6 max-w-4xl text-4xl font-black leading-tight tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-[64px]"
                >
                  PDF Notes, DPPs and Revision Sheets for Every Student
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                  Mediit Study Notes gives students chapter-wise PDFs,
                  handwritten notes, formula sheets, NCERT highlights, daily
                  practice problems and quick revision material for school
                  exams, JEE, NEET and foundation learning.
                </p>

                <p className="sr-only">{seoKeywords}</p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={scrollToNotes}
                    aria-label="Browse study notes library"
                    className="rounded-2xl bg-[#101827] px-7 py-4 text-sm font-black text-white shadow-xl shadow-slate-900/15 transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                  >
                    Browse Notes
                  </button>

                  <button
                    type="button"
                    onClick={goToDashboard}
                    aria-label="Open student dashboard downloads"
                    className="rounded-2xl border border-slate-200 bg-white px-7 py-4 text-sm font-black text-slate-950 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-300"
                  >
                    My Downloads
                  </button>
                </div>

                <div className="mt-9 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="text-3xl font-black text-blue-600">500+</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Notes Available
                    </p>
                  </div>

                  <div className="rounded-3xl border border-green-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="text-3xl font-black text-green-600">PDF</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Download Support
                    </p>
                  </div>

                  <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="text-3xl font-black text-orange-600">DPP</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Daily Practice
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="relative mx-auto max-w-[590px]">
                  <div className="absolute -left-4 top-14 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-2xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Downloads
                    </p>
                    <p className="mt-1 text-2xl font-black text-blue-600">
                      45k+
                    </p>
                  </div>

                  <div className="absolute -right-4 bottom-16 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-2xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Updated
                    </p>
                    <p className="mt-1 text-2xl font-black text-green-600">
                      Daily
                    </p>
                  </div>

                  <div className="rounded-[38px] border border-slate-100 bg-[#101827] p-5 shadow-[0_30px_90px_rgba(15,23,42,0.18)]">
                    <div className="rounded-[30px] bg-white p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-600">
                            Featured Notes
                          </p>
                          <h2 className="mt-2 text-2xl font-black text-slate-950">
                            NEET Biology NCERT Notes
                          </h2>
                          <p className="mt-2 text-sm leading-6 text-slate-500">
                            Line-by-line concepts, diagrams, important terms and
                            MCQ preparation points.
                          </p>
                        </div>

                        <span className="rounded-full border border-green-100 bg-green-50 px-3 py-1 text-xs font-black text-green-600">
                          New
                        </span>
                      </div>

                      <div className="mt-6 grid grid-cols-3 gap-3">
                        <div className="rounded-2xl bg-slate-50 p-4 text-center">
                          <p className="text-2xl font-black text-slate-950">
                            65
                          </p>
                          <p className="mt-1 text-xs font-bold text-slate-500">
                            Pages
                          </p>
                        </div>

                        <div className="rounded-2xl bg-slate-50 p-4 text-center">
                          <p className="text-2xl font-black text-blue-600">
                            PDF
                          </p>
                          <p className="mt-1 text-xs font-bold text-slate-500">
                            Format
                          </p>
                        </div>

                        <div className="rounded-2xl bg-slate-50 p-4 text-center">
                          <p className="text-2xl font-black text-green-600">
                            15k
                          </p>
                          <p className="mt-1 text-xs font-bold text-slate-500">
                            Downloads
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 rounded-3xl bg-[#101827] p-5 text-white">
                        <div className="mb-4 flex items-center justify-between">
                          <p className="text-sm font-black">Notes Progress</p>
                          <p className="text-xs text-white/60">
                            Biology Pack
                          </p>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <div className="mb-2 flex justify-between text-xs font-bold text-white/70">
                              <span>NCERT Highlights</span>
                              <span>92%</span>
                            </div>
                            <div className="h-3 overflow-hidden rounded-full bg-white/10">
                              <div className="h-full w-[92%] rounded-full bg-blue-400" />
                            </div>
                          </div>

                          <div>
                            <div className="mb-2 flex justify-between text-xs font-bold text-white/70">
                              <span>Diagrams</span>
                              <span>84%</span>
                            </div>
                            <div className="h-3 overflow-hidden rounded-full bg-white/10">
                              <div className="h-full w-[84%] rounded-full bg-green-400" />
                            </div>
                          </div>

                          <div>
                            <div className="mb-2 flex justify-between text-xs font-bold text-white/70">
                              <span>MCQ Practice</span>
                              <span>76%</span>
                            </div>
                            <div className="h-3 overflow-hidden rounded-full bg-white/10">
                              <div className="h-full w-[76%] rounded-full bg-orange-400" />
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={goToNotesLibrary}
                        aria-label="Open full notes library"
                        className="mt-5 w-full rounded-2xl bg-blue-600 px-5 py-4 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-blue-700"
                      >
                        Open Notes Library
                      </button>
                    </div>
                  </div>

                  <div className="absolute -bottom-6 left-8 right-8 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-xl sm:block">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-black text-slate-950">
                          Latest Upload
                        </p>
                        <p className="mt-1 text-xs font-bold text-slate-500">
                          Class 10 Science Notes · Updated Today
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={scrollToNotes}
                        aria-label="View latest uploaded study notes"
                        className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-black text-white"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section className="bg-[#f7f9fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-9 grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-6">
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Notes Library
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Everything Students Need for Revision and Practice
                </h2>
              </div>

              <p className="text-sm leading-6 text-slate-500 lg:col-span-6">
                Study Notes are designed to help students revise faster,
                understand concepts clearly and practice important questions
                before school tests, mock exams and competitive exams.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {noteBenefits.map((item, index) => (
                <article
                  key={item.title}
                  title={item.keywords}
                  className="group rounded-[28px] border border-slate-100 bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-lg font-black text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <h3 className="text-xl font-black text-slate-950">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {item.desc}
                  </p>

                  <p className="sr-only">{item.keywords}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* NOTES LIBRARY */}
        <section
          id="notes-library"
          className="bg-white py-12 lg:py-16"
          aria-label="Study notes library by course and exam goal"
        >
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Study Material
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Browse Notes by Course and Exam Goal
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Students can find study notes for boards, foundation classes,
                JEE, NEET, NCERT revision, chapter-wise practice and daily
                learning support.
              </p>
            </div>

            <div
              className="mb-8 flex gap-3 overflow-x-auto pb-2"
              aria-label="Study notes category filters"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  aria-label={`Show ${category} study notes`}
                  className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-black transition-all duration-300 ${
                    activeCategory === category
                      ? "border-[#101827] bg-[#101827] text-white shadow-lg shadow-slate-900/15"
                      : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredNotes.map((note) => (
                <article
                  key={note.id}
                  title={note.keywords}
                  className="group overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_22px_60px_rgba(15,23,42,0.12)]"
                >
                  <div className="relative h-[220px] overflow-hidden">
                    <img
                      src={note.image}
                      alt={note.alt}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />

                    <span
                      className={`absolute left-4 top-4 rounded-full border px-3 py-1 text-xs font-black ${getTypeStyle(
                        note.type
                      )}`}
                    >
                      {note.type}
                    </span>

                    <span className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-black text-slate-950 backdrop-blur">
                      {note.classLevel}
                    </span>

                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-200">
                        {note.subject}
                      </p>
                      <h3 className="mt-2 text-2xl font-black leading-tight tracking-[-0.03em] text-white">
                        {note.title}
                      </h3>
                    </div>
                  </div>

                  <div className="p-5">
                    <p className="text-sm font-bold text-blue-700">
                      {note.seoTitle}
                    </p>

                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      {note.description}
                    </p>

                    <p className="sr-only">{note.keywords}</p>

                    <div className="mt-5 grid grid-cols-3 gap-3">
                      <div className="rounded-2xl bg-slate-50 p-3 text-center">
                        <p className="text-lg font-black text-slate-950">
                          {note.pages}
                        </p>
                        <p className="text-[11px] font-bold text-slate-500">
                          Pages
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-3 text-center">
                        <p className="text-lg font-black text-blue-600">
                          {note.downloads}
                        </p>
                        <p className="text-[11px] font-bold text-slate-500">
                          Downloads
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-3 text-center">
                        <p className="text-lg font-black text-green-600">
                          PDF
                        </p>
                        <p className="text-[11px] font-bold text-slate-500">
                          Format
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 rounded-2xl bg-blue-50 p-4">
                      <p className="text-xs font-bold text-blue-600">Status</p>
                      <p className="mt-1 text-sm font-black text-slate-950">
                        {note.updated}
                      </p>
                    </div>

                    <div className="mt-5 space-y-2">
                      {note.points.map((point) => (
                        <div key={point} className="flex items-center gap-2">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-50 text-xs font-black text-green-600">
                            ✓
                          </span>
                          <p className="text-sm font-medium text-slate-700">
                            {point}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={goToNotesLibrary}
                        aria-label={`View ${note.seoTitle}`}
                        className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-950 transition-all duration-300 hover:-translate-y-1 hover:border-blue-300"
                      >
                        View Notes
                      </button>

                      <button
                        type="button"
                        onClick={goToNotesLibrary}
                        aria-label={`Download ${note.seoTitle}`}
                        className="rounded-2xl bg-[#101827] px-5 py-3 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                      >
                        Download
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* STUDY FLOW + RESOURCES */}
        <section className="bg-[#f7f9fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <div className="rounded-[34px] bg-[#101827] p-7 text-white shadow-2xl lg:p-8">
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-200">
                    Study Workflow
                  </p>

                  <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] sm:text-4xl">
                    Notes Work Best When Students Revise and Practice Together
                  </h2>

                  <p className="mt-4 text-sm leading-7 text-white/70">
                    Mediit Study Notes are connected with live classes, DPPs,
                    quizzes and test preparation so students can learn, revise
                    and practice in one structured flow.
                  </p>

                  <button
                    type="button"
                    onClick={goToDashboard}
                    aria-label="Open student dashboard for notes and practice"
                    className="mt-7 rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all duration-300 hover:-translate-y-1"
                  >
                    Open Student Dashboard
                  </button>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {studyFlow.map((item) => (
                    <article
                      key={item.step}
                      title={item.keywords}
                      className="rounded-[28px] border border-slate-100 bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                    >
                      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-lg font-black text-blue-600">
                        {item.step}
                      </div>

                      <h3 className="text-xl font-black text-slate-950">
                        {item.title}
                      </h3>

                      <p className="mt-3 text-sm leading-6 text-slate-500">
                        {item.desc}
                      </p>

                      <p className="sr-only">{item.keywords}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-10 rounded-[32px] border border-slate-100 bg-white p-6 shadow-sm lg:p-8">
              <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                    Available Resources
                  </p>
                  <h3 className="mt-2 text-2xl font-black text-slate-950">
                    Study Resources Students Can Access
                  </h3>
                </div>

                <p className="max-w-xl text-sm leading-6 text-slate-500">
                  These resources can be connected with your LMS dashboard,
                  course pages, live classes and test series for a complete
                  learning experience.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {resources.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 rounded-2xl bg-[#f8fbff] p-4 transition-all duration-300 hover:-translate-y-1 hover:bg-blue-50"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-sm font-black text-blue-600 shadow-sm">
                      ✓
                    </span>
                    <p className="text-sm font-black text-slate-800">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white py-12">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 p-8 text-white shadow-2xl lg:p-10">
              <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="absolute -bottom-10 left-10 h-44 w-44 rounded-full bg-orange-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-200">
                    Learn. Revise. Practice.
                  </p>

                  <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] sm:text-4xl">
                    Give Students Organized Notes for Every Subject and Exam
                    Goal
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70">
                    Mediit Study Notes provides chapter PDFs, formula sheets,
                    DPPs, handwritten notes and quick revision resources to make
                    preparation easier, faster and more consistent.
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={scrollToNotes}
                    aria-label="Browse available study notes"
                    className="rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all duration-300 hover:-translate-y-1"
                  >
                    Browse Notes
                  </button>

                  <button
                    type="button"
                    onClick={goToDashboard}
                    aria-label="Open student dashboard for notes"
                    className="rounded-2xl border border-white/15 bg-white/10 px-7 py-4 text-sm font-black text-white backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:bg-white/15"
                  >
                    Open Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default StudyNotes;